import { ButtonColors, ButtonSizes, ButtonVariants } from '@/constants/buttonTypes';
import useAnalytics from '@/hooks/use-analytics';
import { AddressFields } from '@/hooks/use-google-address-validation';
import { Box, styled, Typography } from '@mui/material';
import { useState } from 'react';
import { GenericButton } from './formElements/buttons/GenericButton';
import { ModalDialog } from './ModalDialog';

interface AddressValidationModalProps {
  id: string;
  providedAddress: AddressFields;
  suggestedAddress: AddressFields;
  onSelectAddress: (address: AddressFields) => void;
}

export const AddressValidationModal = ({
  id,
  providedAddress,
  suggestedAddress,
  onSelectAddress,
}: AddressValidationModalProps) => {
  const { trackEvent } = useAnalytics();
  const [selectedAddress, setSelectedAddress] = useState<'provided' | 'suggested'>('suggested');

  const handleSelectAddress = () => {
    const address = selectedAddress === 'provided' ? providedAddress : suggestedAddress;
    trackEvent(`ADDRESS_VALIDATION_MODAL_${selectedAddress.toUpperCase()}_SELECTED`);
    onSelectAddress(address);
  };

  const renderAddressOption = (type: 'provided' | 'suggested', label: string, address: AddressFields) => (
    <AddressBox
      isSelected={selectedAddress === type}
      onClick={() => setSelectedAddress(type)}
      role='radio'
      aria-checked={selectedAddress === type}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setSelectedAddress(type);
        }
      }}
    >
      <Typography
        variant='body2'
        fontWeight='bold'
        gutterBottom
      >
        {label}
      </Typography>
      <AddressDisplay address={address} />
    </AddressBox>
  );

  const modalContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', pt: '24px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant='h2'
          textAlign='left'
          sx={{ mb: 1.5 }}
        >
          We found an alternate address
        </Typography>
        <Typography
          variant='body1'
          textAlign='left'
          sx={{ mb: 3 }}
        >
          Which address would you like to use?
        </Typography>
      </Box>

      <AddressComparisonContainer>
        {renderAddressOption('provided', 'You entered', providedAddress)}
        {renderAddressOption('suggested', 'Suggested address', suggestedAddress)}
      </AddressComparisonContainer>
    </Box>
  );

  const footerContent = (
    <GenericButton
      id='select_address_button'
      label='Select address'
      size={ButtonSizes.LARGE}
      color={ButtonColors.PRIMARY}
      variant={ButtonVariants.CONTAINED}
      onClick={handleSelectAddress}
    />
  );

  return (
    <ModalDialog
      id={id}
      status='closed'
      children={modalContent}
      footerChildren={footerContent}
    />
  );
};

const AddressDisplay = ({ address }: { address: AddressFields }) => (
  <Box>
    <Typography variant='body1'>{address.address1}</Typography>
    <Typography variant='body1'>
      {address.city}, {address.state} {address.zip}
    </Typography>
  </Box>
);

const AddressComparisonContainer = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const AddressBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>`
  padding: 20px;
  border: 2px solid
    ${({ isSelected }) => (isSelected ? 'var(--mui-palette-primary-main)' : 'var(--mui-palette-border-main)')};
  border-radius: 12px;
  background-color: var(--mui-palette-background-paper);
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: var(--mui-palette-primary-main);
  }

  &:focus {
    outline: none;
    box-shadow:
      0 0 8px 4px color-mix(in srgb, var(--mui-palette-primary-main) 25%, transparent),
      0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;
